import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { invoicesAPI } from '../api/client';
import apiClient from '../api/client';
import { Search, X, Filter, Calendar, Archive, FileText, AlertTriangle, Trash2, ChevronLeft, ChevronRight, Copy, DollarSign, Upload, Columns3, Check, CreditCard, MessageSquare } from 'lucide-react';
import CurrencyDisplay, { formatCurrencyAmount } from '../components/CurrencyDisplay';
import DeleteInvoiceModal from '../components/DeleteInvoiceModal';
import InvoiceHistoryModal from '../components/InvoiceHistoryModal';
import DataTable from '../components/DataTable';
import { isDepartmentManager, isFinanceAdmin } from '../utils/roleHelpers';

const formatAmount = (amount, currency) => {
  return formatCurrencyAmount(amount, currency);
};

const STORAGE_KEY = 'spocodoc_invoice_columns';

const ALL_COLUMNS = [
  { key: 'documentType', label: 'Type', default: false },
  { key: 'invoiceNumber', label: 'Invoice Number', default: true, locked: true },
  { key: 'vendor', label: 'Vendor', default: true },
  { key: 'department', label: 'Department', default: false },
  { key: 'invoiceDate', label: 'Invoice Date', default: true },
  { key: 'dueDate', label: 'Due Date', default: false },
  { key: 'currency', label: 'Currency', default: true },
  { key: 'amount', label: 'Amount', default: true },
  { key: 'glAccounts', label: 'GL Account', default: false },
  { key: 'budget', label: 'Budget', default: false },
  { key: 'status', label: 'Status', default: true },
  { key: 'approvalStage', label: 'Approval Stage', default: true },
  { key: 'approver', label: 'Approver', default: false },
  { key: 'approvalDate', label: 'Approval Date', default: false },
  { key: 'createdBy', label: 'Created By', default: false },
  { key: 'poNumber', label: 'PO Number', default: false },
  { key: 'erpPaymentStatus', label: 'ERP Payment', default: false },
  { key: 'erpPaidAt', label: 'Paid Date', default: false },
  { key: 'actionRequired', label: 'Action Required', default: true },
  { key: 'actions', label: 'Actions', default: true, locked: true },
];

const DEFAULT_VISIBLE = ALL_COLUMNS.filter(c => c.default).map(c => c.key);

function loadColumnPrefs() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Ensure locked columns are always included
      const locked = ALL_COLUMNS.filter(c => c.locked).map(c => c.key);
      const merged = [...new Set([...locked, ...parsed.filter(k => ALL_COLUMNS.some(c => c.key === k))])];
      return merged;
    }
  } catch {}
  return DEFAULT_VISIBLE;
}

// Current financial year start (UK: April–March). May 2026 → start = 2026 (Apr 2026 – Mar 2027)
const _now = new Date();
const DEFAULT_FY_START = _now.getMonth() >= 3 ? _now.getFullYear() : _now.getFullYear() - 1;
const FY_YEARS = Array.from({ length: 5 }, (_, i) => DEFAULT_FY_START - i);

export default function InvoiceList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [invoices, setInvoices] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('CreatedAt');
  const [sortDir, setSortDir] = useState('DESC');
  
  // Check if user is Department Manager
  const isDeptManager = isDepartmentManager(user);
  const isFinAdmin = isFinanceAdmin(user);

  // Delete modal state
  const [deleteModalInvoice, setDeleteModalInvoice] = useState(null);
  const [commentPopupInvoiceId, setCommentPopupInvoiceId] = useState(null);
  
  // If navigated from the dashboard with an active filter, don't restrict by FY date
  const hasDashboardFilter = !!(location.state?.overdueFilter || location.state?.erpStatusFilter || location.state?.postedStatusFilter || location.state?.vendorFilter || location.state?.filterStatus || location.state?.fyYear);

  // Use dashboard FY year if provided, otherwise default
  const _initFyYear = location.state?.fyYear || null;

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || location.state?.filterStatus || '');
  const [vendorFilter, setVendorFilter] = useState(location.state?.vendorFilter || '');
  const [vendorName, setVendorName] = useState(location.state?.vendorName || '');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [fyFilter, setFyFilter] = useState(_initFyYear ? String(_initFyYear) : (hasDashboardFilter ? '' : String(DEFAULT_FY_START)));
  const [invoiceDateFrom, setInvoiceDateFrom] = useState(_initFyYear ? `${_initFyYear}-04-01` : (hasDashboardFilter ? '' : `${DEFAULT_FY_START}-04-01`));
  const [invoiceDateTo, setInvoiceDateTo] = useState(_initFyYear ? `${_initFyYear + 1}-03-31` : (hasDashboardFilter ? '' : `${DEFAULT_FY_START + 1}-03-31`));
  const [createdDateFrom, setCreatedDateFrom] = useState('');
  const [createdDateTo, setCreatedDateTo] = useState('');
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [approvalStageFilter, setApprovalStageFilter] = useState('');
  const [dataMismatchFilter, setDataMismatchFilter] = useState(false);
  const [documentTypeFilter, setDocumentTypeFilter] = useState('');
  const [overdueFilter, setOverdueFilter] = useState(location.state?.overdueFilter || false);
  const [erpStatusFilter, setErpStatusFilter] = useState(searchParams.get('erpStatus') || location.state?.erpStatusFilter || '');
  const [postedStatusFilter, setPostedStatusFilter] = useState(location.state?.postedStatusFilter || '');
  const [showFilters, setShowFilters] = useState(
    !!(location.state?.overdueFilter || location.state?.erpStatusFilter || location.state?.postedStatusFilter || location.state?.vendorFilter || location.state?.filterStatus)
  );
  const [showDeleted, setShowDeleted] = useState(false);
  
  // Duplicate group navigation
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);

  // Inline ERP payment status editing
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [savingPaymentId, setSavingPaymentId] = useState(null);
  const erpSaveInProgressRef = useRef(false);

  const handleInlinePaymentSave = async (invoiceId, newStatus) => {
    if (!newStatus) { setEditingPaymentId(null); return; }
    erpSaveInProgressRef.current = true;
    setSavingPaymentId(invoiceId);
    try {
      await apiClient.patch(`/invoices/${invoiceId}/payment-status`, { ERPPaymentStatus: newStatus });
      setInvoices(prev => prev.map(inv =>
        inv.InvoiceID === invoiceId ? { ...inv, ERPPaymentStatus: newStatus } : inv
      ));
    } catch (err) {
      console.error('Failed to update payment status', err);
    } finally {
      erpSaveInProgressRef.current = false;
      setSavingPaymentId(null);
      setEditingPaymentId(null);
    }
  };

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(loadColumnPrefs);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const columnPickerRef = useRef(null);

  useEffect(() => {
    // Update filter if coming from navigation with vendor filter
    if (location.state?.vendorFilter) {
      setVendorFilter(location.state.vendorFilter);
      setVendorName(location.state.vendorName || '');
    }
  }, [location.state]);

  // Sync filters from URL only when searchParams actually changes after mount
  // (not on initial render — initial values are already set via useState above)
  const searchParamsMountedRef = useRef(false);
  useEffect(() => {
    if (!searchParamsMountedRef.current) { searchParamsMountedRef.current = true; return; }
    setStatusFilter(searchParams.get('status') || '');
    setErpStatusFilter(searchParams.get('erpStatus') || '');
  }, [searchParams]);

  useEffect(() => {
    fetchVendors();
    fetchDepartments();
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [page, sortBy, sortDir, vendorFilter, departmentFilter, statusFilter, approvalStageFilter, invoiceDateFrom, invoiceDateTo, createdDateFrom, createdDateTo, searchTerm, showDuplicates, dataMismatchFilter, showDeleted, documentTypeFilter, overdueFilter, erpStatusFilter, postedStatusFilter]);

  // Close column picker on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target)) {
        setShowColumnPicker(false);
      }
    };
    if (showColumnPicker) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showColumnPicker]);

  const toggleColumn = (key) => {
    const col = ALL_COLUMNS.find(c => c.key === key);
    if (col?.locked) return;
    setVisibleColumns(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const resetColumns = () => {
    setVisibleColumns(DEFAULT_VISIBLE);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_VISIBLE));
  };

  const isColumnVisible = (key) => visibleColumns.includes(key);

  const fetchVendors = async () => {
    try {
      let response;
      if (isDeptManager) {
        // Department Managers only see vendors used in their departments
        response = await apiClient.get('/vendors/my-department');
        const vendorList = response.data.vendors || response.data || [];
        setVendors(Array.isArray(vendorList) ? vendorList : []);
      } else {
        // Regular users see all company vendors
        response = await apiClient.get('/vendors', {
          params: { page: 1, pageSize: 1000, includeAll: true }
        });
        const vendorList = response.data.vendors || response.data || [];
        setVendors(Array.isArray(vendorList) ? vendorList : []);
      }
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
      setVendors([]);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await apiClient.get('/departments');
      const deptData = response.data?.departments || response.data || [];
      setDepartments(Array.isArray(deptData) ? deptData : []);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
      setDepartments([]);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = { 
        page, 
        pageSize: 20,
        sortBy,
        sortDir
      };
      
      // Department Managers can only see their department's invoices
      let items = [];
      if (isDeptManager) {
        // Use department-specific endpoint
        if (statusFilter) params.status = statusFilter;
        if (invoiceDateFrom) params.dateFrom = invoiceDateFrom;
        if (invoiceDateTo) params.dateTo = invoiceDateTo;
        if (searchTerm) params.search = searchTerm;
        
        const response = await apiClient.get('/invoices/my-department', { params });
        items = response.data.invoices || [];
        setInvoices(items);
        setTotal(response.data.total || 0);
        setTotalPages(Math.ceil((response.data.total || 0) / 20));
      } else {
        // Regular users see all company invoices
        if (vendorFilter) params.vendorId = vendorFilter;
        if (departmentFilter) params.departmentId = departmentFilter;
        if (statusFilter) params.status = statusFilter;
        if (approvalStageFilter) params.approvalStage = approvalStageFilter;
        if (dataMismatchFilter) params.dataMismatch = 'true';
        if (invoiceDateFrom) params.dateFrom = invoiceDateFrom;
        if (invoiceDateTo) params.dateTo = invoiceDateTo;
        if (createdDateFrom) params.createdFrom = createdDateFrom;
        if (createdDateTo) params.createdTo = createdDateTo;
        if (searchTerm) params.search = searchTerm;
        if (showDuplicates) params.showDuplicates = 'true';
        if (showDeleted) params.showDeleted = 'true';
        if (documentTypeFilter) params.documentType = documentTypeFilter;
        if (overdueFilter) params.overdue = 'true';
        if (erpStatusFilter) params.erpStatus = erpStatusFilter;
        if (postedStatusFilter) params.postedStatus = postedStatusFilter;

        const response = await invoicesAPI.getAll(params);
        items = response.data.items || [];
        setInvoices(items);
        setTotalPages(response.data.totalPages || 1);
        setTotal(response.data.total || 0);
      }
      
      // Identify duplicate groups when showing duplicates
      if (showDuplicates) {
        const groups = [];
        const seen = new Set();
        
        items.forEach(invoice => {
          if (invoice.IsDuplicate === 1) {
            const groupKey = `${invoice.InvoiceNumber}-${invoice.VendorID}`;
            if (!seen.has(groupKey)) {
              seen.add(groupKey);
              groups.push({
                invoiceNumber: invoice.InvoiceNumber,
                vendorId: invoice.VendorID,
                vendorName: invoice.VendorName
              });
            }
          }
        });
        
        setDuplicateGroups(groups);
        setCurrentGroupIndex(0);
      } else {
        setDuplicateGroups([]);
        setCurrentGroupIndex(0);
      }
    } catch (error) {
      console.error('Fetch invoices error:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFYChange = (val) => {
    setFyFilter(val);
    if (!val) {
      setInvoiceDateFrom('');
      setInvoiceDateTo('');
    } else {
      const yr = parseInt(val);
      setInvoiceDateFrom(`${yr}-04-01`);
      setInvoiceDateTo(`${yr + 1}-03-31`);
    }
    setPage(1);
  };

  // Reset to page 1 whenever any filter changes (prevents showing page 3 of a 1-page filtered result)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, vendorFilter, departmentFilter]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setVendorFilter('');
    setVendorName('');
    setDepartmentFilter('');
    setFyFilter(String(DEFAULT_FY_START));
    setInvoiceDateFrom(`${DEFAULT_FY_START}-04-01`);
    setInvoiceDateTo(`${DEFAULT_FY_START + 1}-03-31`);
    setCreatedDateFrom('');
    setCreatedDateTo('');
    setShowDuplicates(false);
    setApprovalStageFilter('');
    setDataMismatchFilter(false);
    setShowDeleted(false);
    setDocumentTypeFilter('');
    setOverdueFilter(false);
    setErpStatusFilter('');
    setPostedStatusFilter('');
    setPage(1);
  };

  const hasActiveFilters = () => {
    return searchTerm || statusFilter || vendorFilter || departmentFilter ||
           fyFilter !== String(DEFAULT_FY_START) ||
           createdDateFrom || createdDateTo || showDuplicates || overdueFilter || erpStatusFilter || postedStatusFilter ||
           approvalStageFilter || dataMismatchFilter || showDeleted;
  };

  // Get invoices filtered by current duplicate group
  const getFilteredInvoices = () => {
    let filtered = invoices;
    
    // Filter by duplicate group if active
    if (showDuplicates && duplicateGroups.length > 0) {
      const currentGroup = duplicateGroups[currentGroupIndex];
      if (currentGroup) {
        filtered = filtered.filter(inv => 
          inv.InvoiceNumber === currentGroup.invoiceNumber && 
          inv.VendorID === currentGroup.vendorId
        );
      }
    }
    
    // Filter by overall status
    if (statusFilter) {
      filtered = filtered.filter(inv => (inv.Status || 'New') === statusFilter);
    }
    
    // Filter by approval stage
    if (approvalStageFilter) {
      filtered = filtered.filter(inv => (inv.ApprovalStage || 'Not Started') === approvalStageFilter);
    }
    
    // Filter by data mismatch
    if (dataMismatchFilter) {
      filtered = filtered.filter(inv => hasDataMismatch(inv));
    }
    
    return filtered;
  };

  const navigateGroup = (direction) => {
    const newIndex = currentGroupIndex + direction;
    if (newIndex >= 0 && newIndex < duplicateGroups.length) {
      setCurrentGroupIndex(newIndex);
    }
  };

  const hasDataMismatch = (invoice) => {
    const subTotal = parseFloat(invoice.SubTotal) || 0;
    const taxAmount = parseFloat(invoice.TaxAmount) || 0;
    const totalAmount = parseFloat(invoice.TotalAmount) || 0;
    const calculatedTotal = subTotal + taxAmount;
    // Allow for small floating point differences (0.01)
    return Math.abs(calculatedTotal - totalAmount) > 0.01;
  };

  const handleDelete = async (deleteType) => {
    if (!deleteModalInvoice) return;
    try {
      await invoicesAPI.delete(deleteModalInvoice.InvoiceID, deleteType);
      setDeleteModalInvoice(null);
      alert(deleteType === 'hard' ? 'Invoice permanently deleted' : 'Invoice archived successfully');
      fetchInvoices(); // Refresh the list
    } catch (error) {
      console.error('Delete invoice error:', error);
      alert(error.response?.data?.message || 'Failed to delete invoice');
      throw error;
    }
  };

  if (loading && invoices.length === 0) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <>
    <div className="space-y-6">
      {/* Back navigation */}
      {location.state?.from && (
        <button
          onClick={() => navigate(location.state.from)}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium group"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to {location.state.fromLabel || 'Previous Page'}
        </button>
      )}
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <FileText className="w-7 h-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
            {showDeleted && (
              <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded">
                <Trash2 className="w-3 h-3" /> Viewing Deleted Invoices
              </div>
            )}
            <p className="text-sm text-gray-500 mt-1">
              {showDuplicates && duplicateGroups.length > 0 ? (
                <>
                  Showing {getFilteredInvoices().length} invoices in current duplicate group 
                  <span className="text-orange-600 ml-1">(Group {currentGroupIndex + 1} of {duplicateGroups.length})</span>
                </>
              ) : (
                <>
                  Showing {invoices.length} of {total} invoices
                  {hasActiveFilters() && <span className="text-blue-600 ml-1">(filtered)</span>}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 text-sm font-medium rounded-lg border flex items-center gap-2 ${
              showFilters 
                ? 'bg-blue-600 text-white border-blue-600' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {hasActiveFilters() && !showFilters && (
              <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                !
              </span>
            )}
          </button>
          {/* Column Picker */}
          <div className="relative" ref={columnPickerRef}>
            <button
              onClick={() => setShowColumnPicker(!showColumnPicker)}
              className={`px-4 py-2 text-sm font-medium rounded-lg border flex items-center gap-2 ${
                showColumnPicker
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Columns3 className="w-4 h-4" />
              Columns
            </button>
            {showColumnPicker && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-2">
                <div className="px-3 py-1.5 flex items-center justify-between border-b border-gray-100 mb-1">
                  <span className="text-xs font-semibold text-gray-500 uppercase">Toggle Columns</span>
                  <button onClick={resetColumns} className="text-xs text-blue-600 hover:text-blue-800">Reset</button>
                </div>
                {ALL_COLUMNS.map(col => (
                  <button
                    key={col.key}
                    onClick={() => toggleColumn(col.key)}
                    disabled={col.locked}
                    className={`w-full px-3 py-1.5 text-left text-sm flex items-center gap-2 hover:bg-gray-50 ${col.locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      visibleColumns.includes(col.key) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                    }`}>
                      {visibleColumns.includes(col.key) && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {col.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {isFinAdmin && (
            <Link
              to="/invoices/queue"
              className="px-4 py-2 text-sm font-medium rounded-lg border bg-white text-gray-700 border-gray-300 hover:bg-gray-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              Processing Queue
            </Link>
          )}
          {hasPermission('upload_invoices') && (
            <>
              <Link 
                to="/invoices/upload" 
                className="px-4 py-2 text-sm font-medium rounded-lg border bg-white text-gray-700 border-gray-300 hover:bg-gray-50 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Upload Invoices
              </Link>
              <Link 
                to="/invoices/upload?type=CreditNote" 
                className="px-4 py-2 text-sm font-medium rounded-lg border bg-white text-teal-700 border-teal-300 hover:bg-teal-50 flex items-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                Upload Credit Notes
              </Link>
            </>
          )}
          <Link 
            to="/invoices/new" 
            className="btn btn-primary"
          >
            <FileText className="w-4 h-4" />
            New Invoice
          </Link>
        </div>
      </div>

      {/* Financial Year quick-select — dropdown */}
      <div className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 px-4 py-2.5 shadow-sm flex-wrap">
        <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className="text-sm font-medium text-gray-600">FY:</span>
        <select
          value={fyFilter}
          onChange={(e) => handleFYChange(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700 font-medium"
        >
          {FY_YEARS.map(yr => (
            <option key={yr} value={String(yr)}>
              {yr}/{String(yr + 1).slice(-2)}{yr === DEFAULT_FY_START ? ' (Current)' : ''}
            </option>
          ))}
          <option value="">All Time</option>
        </select>
        {fyFilter !== String(DEFAULT_FY_START) && fyFilter !== '' && fyFilter !== 'custom' && (
          <button
            onClick={() => handleFYChange(String(DEFAULT_FY_START))}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Reset to current FY
          </button>
        )}
      </div>

      {/* Document Type filter tabs */}
      <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 px-3 py-2 shadow-sm">
        {[
          { value: '', label: 'All Documents' },
          { value: 'Invoice', label: 'Invoices' },
          { value: 'CreditNote', label: 'Credit Notes' },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => { setDocumentTypeFilter(tab.value); setPage(1); }}
            className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors ${
              documentTypeFilter === tab.value
                ? tab.value === 'CreditNote'
                  ? 'bg-teal-600 text-white border-teal-600'
                  : 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            {hasActiveFilters() && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear All Filters
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div className="lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Invoice number, vendor, or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Vendor Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
              <select
                value={vendorFilter}
                onChange={(e) => {
                  setVendorFilter(e.target.value);
                  const selected = vendors.find(v => v.VendorID === e.target.value);
                  setVendorName(selected ? selected.VendorName : '');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Vendors</option>
                {vendors.map(vendor => (
                  <option key={vendor.VendorID} value={vendor.VendorID}>
                    {vendor.VendorName}
                  </option>
                ))}
              </select>
            </div>

            {/* Department Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.DepartmentID} value={dept.DepartmentID}>
                    {dept.DepartmentName || dept.Name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Posted">Posted</option>
              </select>
            </div>

            {/* ERP Posting Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ERP Posting</label>
              <select
                value={postedStatusFilter}
                onChange={(e) => setPostedStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="Posted">Posted</option>
                <option value="Not Posted">Not Posted</option>
                <option value="Failed">Failed</option>
              </select>
            </div>

            {/* Financial Year quick-select inside filter panel */}
            <div className="lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Financial Year
              </label>
              <select
                value={fyFilter}
                onChange={(e) => handleFYChange(e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700"
              >
                {FY_YEARS.map(yr => (
                  <option key={yr} value={String(yr)}>
                    FY {yr}/{String(yr + 1).slice(-2)}{yr === DEFAULT_FY_START ? ' (Current)' : ''}
                  </option>
                ))}
                <option value="">All Time</option>
              </select>
            </div>

            {/* Invoice Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Invoice Date From
              </label>
              <input
                type="date"
                value={invoiceDateFrom}
                onChange={(e) => { setInvoiceDateFrom(e.target.value); setFyFilter('custom'); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Invoice Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Invoice Date To
              </label>
              <input
                type="date"
                value={invoiceDateTo}
                onChange={(e) => { setInvoiceDateTo(e.target.value); setFyFilter('custom'); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Created Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Created Date From
              </label>
              <input
                type="date"
                value={createdDateFrom}
                onChange={(e) => setCreatedDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Created Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                Created Date To
              </label>
              <input
                type="date"
                value={createdDateTo}
                onChange={(e) => setCreatedDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Overall Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Overall Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="New">New</option>
                <option value="In Approval">In Approval</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="On Hold">On Hold</option>
                <option value="Archived">Archived</option>
              </select>
            </div>
            
            {/* Approval Stage Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Approval Stage</label>
              <select
                value={approvalStageFilter}
                onChange={(e) => setApprovalStageFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All</option>
                <option value="Not Started">Not Started</option>
                <option value="Awaiting Department">Awaiting Department</option>
                <option value="Awaiting GL Owner">Awaiting GL Owner</option>
                <option value="Awaiting Finance">Awaiting Finance</option>
                <option value="Completed">Completed</option>
                <option value="Returned">Returned</option>
              </select>
            </div>

            {/* Show Duplicates Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Duplicate Invoices
              </label>
              <div className="flex items-center h-[42px]">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showDuplicates}
                    onChange={(e) => setShowDuplicates(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {showDuplicates ? 'Duplicates Only' : 'All'}
                  </span>
                </label>
              </div>
            </div>

            {/* Data Mismatch Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Data Mismatch
              </label>
              <div className="flex items-center h-[42px]">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dataMismatchFilter}
                    onChange={(e) => setDataMismatchFilter(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {dataMismatchFilter ? 'Mismatch Only' : 'All'}
                  </span>
                </label>
              </div>
            </div>

            {/* Show Deleted - Finance Admin only */}
            {isFinAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Trash2 className="w-4 h-4 inline mr-1" />
                  Deleted Invoices
                </label>
                <div className="flex items-center h-[42px]">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showDeleted}
                      onChange={(e) => { setShowDeleted(e.target.checked); setPage(1); }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
                    <span className="ml-3 text-sm font-medium text-gray-700">
                      {showDeleted ? 'Showing Deleted' : 'Active Only'}
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Active Filter Tags */}
      {hasActiveFilters() && !showFilters && (
        <div className="flex flex-wrap gap-2">
          {searchTerm && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Search: "{searchTerm}"
              <button onClick={() => setSearchTerm('')} className="hover:text-blue-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {vendorName && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Vendor: {vendorName}
              <button onClick={() => { setVendorFilter(''); setVendorName(''); }} className="hover:text-blue-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {statusFilter && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Status: {statusFilter}
              <button onClick={() => setStatusFilter('')} className="hover:text-blue-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {approvalStageFilter && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Approval: {approvalStageFilter}
              <button onClick={() => setApprovalStageFilter('')} className="hover:text-blue-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {(fyFilter === '' || fyFilter === 'custom') && (invoiceDateFrom || invoiceDateTo) && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              <Calendar className="w-3 h-3" />
              {fyFilter === 'custom' ? 'Custom date range' : invoiceDateFrom || invoiceDateTo}
              <button onClick={() => handleFYChange(String(DEFAULT_FY_START))} className="hover:text-blue-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {fyFilter && fyFilter !== String(DEFAULT_FY_START) && fyFilter !== 'custom' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              <Calendar className="w-3 h-3" />
              FY {fyFilter}/{String(parseInt(fyFilter) + 1).slice(-2)}
              <button onClick={() => handleFYChange(String(DEFAULT_FY_START))} className="hover:text-blue-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {showDuplicates && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
              <AlertTriangle className="w-4 h-4" />
              Duplicates
              <button onClick={() => setShowDuplicates(false)} className="hover:text-orange-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {dataMismatchFilter && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
              <AlertTriangle className="w-4 h-4" />
              Data Mismatch
              <button onClick={() => setDataMismatchFilter(false)} className="hover:text-red-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {showDeleted && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
              <Trash2 className="w-4 h-4" />
              Showing Deleted
              <button onClick={() => setShowDeleted(false)} className="hover:text-red-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {overdueFilter && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
              <AlertTriangle className="w-4 h-4" />
              Overdue Only
              <button onClick={() => setOverdueFilter(false)} className="hover:text-red-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {erpStatusFilter && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
              ERP: {erpStatusFilter}
              <button onClick={() => setErpStatusFilter('')} className="hover:text-purple-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
          {postedStatusFilter && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
              ERP Posting: {postedStatusFilter}
              <button onClick={() => setPostedStatusFilter('')} className="hover:text-green-900">
                <X className="w-4 h-4" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Invoices Table */}
      <div className="card p-0">
        {showDuplicates && duplicateGroups.length > 0 && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <span className="text-sm font-medium text-orange-800 whitespace-nowrap">
                Group {currentGroupIndex + 1}/{duplicateGroups.length}
              </span>
              <span className="text-sm text-orange-700 truncate">
                {duplicateGroups[currentGroupIndex]?.invoiceNumber} - {duplicateGroups[currentGroupIndex]?.vendorName}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => navigateGroup(-1)}
                disabled={currentGroupIndex === 0}
                className="p-1.5 rounded hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous duplicate group"
              >
                <ChevronLeft className="w-4 h-4 text-orange-700" />
              </button>
              <button
                onClick={() => navigateGroup(1)}
                disabled={currentGroupIndex === duplicateGroups.length - 1}
                className="p-1.5 rounded hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next duplicate group"
              >
                <ChevronRight className="w-4 h-4 text-orange-700" />
              </button>
            </div>
          </div>
        )}

        <DataTable
          rows={getFilteredInvoices()}
          rowKey={(r) => r.InvoiceID}
          loading={loading}
          emptyMessage="No invoices found"
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={(key, dir) => { setSortBy(key); setSortDir(dir); setPage(1); }}
          page={page}
          pageSize={20}
          total={total}
          onPageChange={setPage}
          tableMinWidth={Math.max(900, visibleColumns.length * 130)}
          columns={[
            isColumnVisible('documentType') && {
              key: 'documentType',
              label: 'Type',
              render: (inv) => {
                const dt = inv.DocumentType || 'Invoice';
                if (dt === 'CreditNote') {
                  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">Credit Note</span>;
                }
                return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Invoice</span>;
              },
            },
            isColumnVisible('invoiceNumber') && {
              key: 'invoiceNumber',
              label: 'Invoice Number',
              sortable: true,
              sortKey: 'InvoiceNumber',
              sticky: 'left',
              cellClassName: 'font-medium',
              render: (inv) => inv.InvoiceNumber,
            },
            isColumnVisible('vendor') && {
              key: 'vendor',
              label: 'Vendor',
              render: (inv) => inv.VendorName,
            },
            isColumnVisible('department') && {
              key: 'department',
              label: 'Department',
              render: (inv) => inv.DepartmentName || <span className="text-gray-400">—</span>,
            },
            isColumnVisible('invoiceDate') && {
              key: 'invoiceDate',
              label: 'Invoice Date',
              sortable: true,
              sortKey: 'InvoiceDate',
              render: (inv) => new Date(inv.InvoiceDate).toLocaleDateString(),
            },
            isColumnVisible('dueDate') && {
              key: 'dueDate',
              label: 'Due Date',
              sortable: true,
              sortKey: 'DueDate',
              render: (inv) => inv.DueDate ? new Date(inv.DueDate).toLocaleDateString() : <span className="text-gray-400">—</span>,
            },
            isColumnVisible('currency') && {
              key: 'currency',
              label: 'Currency',
              sortable: true,
              sortKey: 'Currency',
              render: (inv) => inv.Currency,
            },
            isColumnVisible('amount') && {
              key: 'amount',
              label: 'Amount',
              align: 'right',
              sortable: true,
              sortKey: 'TotalAmount',
              cellClassName: 'font-medium',
              render: (inv) => (
                <CurrencyDisplay
                  displayAmount={inv.DisplayAmount || inv.TotalAmount}
                  defaultCurrency={inv.Currency}
                  originalAmount={inv.OriginalAmount}
                  originalCurrency={inv.OriginalCurrency}
                  exchangeRate={inv.ExchangeRate}
                />
              ),
            },
            isColumnVisible('glAccounts') && {
              key: 'glAccounts',
              label: 'GL Account',
              render: (inv) => inv.GLAccounts ? (
                <span className="text-xs font-mono text-emerald-700" title={inv.GLAccounts}>
                  {inv.GLAccounts.length > 30 ? inv.GLAccounts.substring(0, 30) + '...' : inv.GLAccounts}
                </span>
              ) : (
                <span className="text-xs text-amber-600">Not assigned</span>
              ),
            },
            isColumnVisible('budget') && {
              key: 'budget',
              label: 'Budget',
              render: (inv) => inv.HasBudget ? (
                <span className="text-xs text-emerald-700" title={inv.BudgetNames}>
                  {inv.BudgetNames && inv.BudgetNames.length > 25 ? inv.BudgetNames.substring(0, 25) + '...' : (inv.BudgetNames || 'Assigned')}
                </span>
              ) : (
                <span className="text-xs text-amber-600">Not assigned</span>
              ),
            },
            isColumnVisible('status') && {
              key: 'status',
              label: 'Status',
              sortable: true,
              sortKey: 'Status',
              render: (inv) => (
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                  (inv.Status === 'Approved' || inv.Status === 'Archived') ? 'bg-green-100 text-green-800' :
                  inv.Status === 'Rejected' ? 'bg-red-100 text-red-800' :
                  inv.Status === 'In Approval' ? 'bg-blue-100 text-blue-800' :
                  inv.Status === 'On Hold' ? 'bg-orange-100 text-orange-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {inv.Status || 'New'}
                </span>
              ),
            },
            showDeleted && {
              key: 'deletedAt',
              label: 'Deleted At',
              render: (inv) => <span className="text-gray-600">{inv.DeletedAt ? new Date(inv.DeletedAt).toLocaleDateString() : '-'}</span>,
            },
            showDeleted && {
              key: 'deletedBy',
              label: 'Deleted By',
              render: (inv) => <span className="text-gray-600">{inv.DeletedByName || '-'}</span>,
            },
            !showDeleted && isColumnVisible('approvalStage') && {
              key: 'approvalStage',
              label: 'Approval Stage',
              sortable: true,
              sortKey: 'ApprovalStage',
              render: (inv) => (
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                  inv.ApprovalStage === 'Completed' ? 'bg-green-100 text-green-800' :
                  inv.ApprovalStage === 'Returned' ? 'bg-red-100 text-red-800' :
                  (inv.ApprovalStage === 'Awaiting Department' || inv.ApprovalStage === 'Awaiting GL Owner' || inv.ApprovalStage === 'Awaiting Finance') ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {inv.ApprovalStage || 'Not Started'}
                </span>
              ),
            },
            !showDeleted && isColumnVisible('approver') && {
              key: 'approver',
              label: 'Approver',
              render: (inv) => inv.CurrentApprover || <span className="text-gray-400">—</span>,
            },
            !showDeleted && isColumnVisible('approvalDate') && {
              key: 'approvalDate',
              label: 'Approval Date',
              render: (inv) => inv.LastApprovalDate ? new Date(inv.LastApprovalDate).toLocaleDateString() : <span className="text-gray-400">—</span>,
            },
            !showDeleted && isColumnVisible('actionRequired') && {
              key: 'actionRequired',
              label: 'Action Required',
              align: 'center',
              render: (inv) => {
                const hasMismatch = hasDataMismatch(inv);
                const showRate = inv.ExchangeRateConfirmed === 0 && inv.OriginalCurrency && inv.OriginalCurrency !== inv.Currency;
                return (
                  <div className="flex items-center justify-center gap-2">
                    {inv.IsDuplicate === 1 && (
                      <span title="Duplicate invoice detected" className="cursor-help"><Copy className="w-4 h-4 text-orange-600" /></span>
                    )}
                    {hasMismatch && (
                      <span title="Data mismatch: SubTotal + Tax ≠ Total" className="cursor-help"><AlertTriangle className="w-4 h-4 text-red-600" /></span>
                    )}
                    {inv.VendorApprovalStatus === 'Pending' && (
                      <span title="Vendor pending approval" className="cursor-help"><AlertTriangle className="w-4 h-4 text-yellow-600" /></span>
                    )}
                    {inv.VendorApprovalStatus === 'Rejected' && (
                      <span title="Vendor rejected" className="cursor-help"><AlertTriangle className="w-4 h-4 text-red-700" /></span>
                    )}
                    {showRate && (
                      <span title="Exchange rate needs bank confirmation" className="cursor-help"><DollarSign className="w-4 h-4 text-purple-600" /></span>
                    )}
                    {!inv.IsDuplicate && !hasMismatch && inv.VendorApprovalStatus !== 'Pending' && inv.VendorApprovalStatus !== 'Rejected' && !showRate && (
                      <span className="text-gray-400 text-xs">-</span>
                    )}
                  </div>
                );
              },
            },
            isColumnVisible('createdBy') && {
              key: 'createdBy',
              label: 'Created By',
              render: (inv) => inv.CreatedByName || inv.CreatedBy || inv.CreatedByUsername || <span className="text-gray-400">—</span>,
            },
            isColumnVisible('poNumber') && {
              key: 'poNumber',
              label: 'PO Number',
              sortable: true,
              sortKey: 'PurchaseOrder',
              render: (inv) => inv.PurchaseOrder || <span className="text-gray-400">—</span>,
            },
            isColumnVisible('erpPaymentStatus') && {
              key: 'erpPaymentStatus',
              label: 'ERP Payment',
              render: (inv) => {
                const s = inv.ERPPaymentStatus;
                const canEdit = hasPermission('edit_invoices');
                const isEditing = editingPaymentId === inv.InvoiceID;
                const isSaving = savingPaymentId === inv.InvoiceID;

                if (isEditing) {
                  return (
                    <select
                      autoFocus
                      disabled={isSaving}
                      defaultValue={s || ''}
                      onBlur={() => { if (!erpSaveInProgressRef.current) setEditingPaymentId(null); }}
                      onChange={(e) => handleInlinePaymentSave(inv.InvoiceID, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setEditingPaymentId(null); } }}
                      className="text-xs border border-primary-400 rounded px-1 py-0.5 focus:outline-none"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="">— Clear —</option>
                      <option value="Open">Open</option>
                      <option value="Paid">Paid</option>
                      <option value="PartiallyPaid">PartiallyPaid</option>
                      <option value="Voided">Voided</option>
                    </select>
                  );
                }

                const cls =
                  s === 'Paid'          ? 'bg-green-100 text-green-800' :
                  s === 'PartiallyPaid' ? 'bg-yellow-100 text-yellow-800' :
                  s === 'Voided'        ? 'bg-red-100 text-red-700' :
                  s === 'Open'          ? 'bg-gray-100 text-gray-600' : '';

                return canEdit ? (
                  <button
                    title="Click to edit"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setEditingPaymentId(inv.InvoiceID); }}
                    className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${cls || 'text-gray-400'} hover:ring-1 hover:ring-primary-400 transition-all`}
                  >
                    {s ? (s === 'PartiallyPaid' ? 'Part. Paid' : s) : '—'}
                  </button>
                ) : (
                  s ? <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{s === 'PartiallyPaid' ? 'Part. Paid' : s}</span>
                    : <span className="text-gray-400">—</span>
                );
              },
            },
            isColumnVisible('erpPaidAt') && {
              key: 'erpPaidAt',
              label: 'Paid Date',
              sortable: true,
              sortKey: 'ERPPaidAt',
              render: (inv) => inv.ERPPaidAt ? new Date(inv.ERPPaidAt).toLocaleDateString() : <span className="text-gray-400">—</span>,
            },
            isColumnVisible('actions') && {
              key: 'actions',
              label: 'Actions',
              sticky: 'right',
              render: (inv) => (
                <div className="flex items-center gap-2">
                  <Link to={`/invoices/${inv.InvoiceID}`} className="text-primary-600 hover:text-primary-700">View</Link>
                  <button
                    onClick={() => setCommentPopupInvoiceId(inv.InvoiceID)}
                    className={`relative ${inv.CommentCount > 0 ? 'text-blue-500 hover:text-blue-700' : 'text-gray-300 hover:text-gray-500'}`}
                    title={inv.CommentCount > 0 ? `${inv.CommentCount} approval comment${inv.CommentCount !== 1 ? 's' : ''} — click for full history` : 'View document history'}
                  >
                    <MessageSquare className="w-4 h-4" />
                    {inv.CommentCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white text-[9px] rounded-full w-3.5 h-3.5 flex items-center justify-center leading-none font-bold">
                        {inv.CommentCount > 9 ? '9+' : inv.CommentCount}
                      </span>
                    )}
                  </button>
                  {!showDeleted && hasPermission('delete_invoices') && (
                    <button onClick={() => setDeleteModalInvoice(inv)} className="text-red-600 hover:text-red-800" title="Delete invoice">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ),
            },
          ].filter(Boolean)}
        />
      </div>
    </div>

    {/* Delete Invoice Modal */}
    {deleteModalInvoice && (
      <DeleteInvoiceModal
        invoice={deleteModalInvoice}
        onConfirm={handleDelete}
        onClose={() => setDeleteModalInvoice(null)}
      />
    )}

    {/* Document History Modal — fixed overlay, always on top */}
    {commentPopupInvoiceId && (
      <InvoiceHistoryModal
        invoiceId={commentPopupInvoiceId}
        onClose={() => setCommentPopupInvoiceId(null)}
      />
    )}
    </>
  );
}
